import React, { useState, useEffect } from 'react';
import {
  alpha,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useGetTenantIdQuery } from '@/services/graphql';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleTheme, recordPageVisit } from '@/store/slices/uiSlice';
import TenantSwitcher from './TenantSwitcher';
import PageSearch from './PageSearch';
import { getVisiblePageGroups, type PageGroup } from '@/config/pageRegistry';
import { ENV } from '@/config/environment';

const drawerWidth = 260;

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const dispatch = useAppDispatch();
  const isDarkMode = useAppSelector((state) => state.ui.theme === 'dark');

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Track page visits for the dashboard's recent pages
  useEffect(() => {
    if (location.pathname !== '/') {
      dispatch(recordPageVisit(location.pathname));
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- dispatch is stable

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const { data: tenantIdData } = useGetTenantIdQuery();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const handleToggleTheme = () => {
    dispatch(toggleTheme());
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleToggleGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupLabel]: !prev[groupLabel] }));
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Special handling for SFTP Sync vs Iteration Logs
    if (path === '/sftp-sync') {
      return location.pathname === '/sftp-sync' || location.pathname === '/sftp-sync/config';
    }
    if (path === '/sftp-sync/logs') {
      return location.pathname === '/sftp-sync/logs';
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (group: PageGroup) =>
    group.items.some((item) => isActive(item.path));

  const isGroupExpanded = (group: PageGroup) =>
    expandedGroups[group.label] ?? isGroupActive(group);

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/company.svg"
            alt="Silver Fern"
            sx={{
              height: 32,
              width: 32,
              filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            WSAPI Admin
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List disablePadding>
        {getVisiblePageGroups().map((group) => {
          // Single-item groups (Dashboard) render as a direct link
          if (group.items.length === 1) {
            const item = group.items[0];
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    mx: 1,
                    my: 0.25,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path)
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path) ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          }

          // Multi-item groups render as collapsible sections
          const expanded = isGroupExpanded(group);
          const groupActive = isGroupActive(group);
          return (
            <React.Fragment key={group.label}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleToggleGroup(group.label)}
                  sx={{
                    mx: 1,
                    my: 0.25,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: groupActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      fontWeight: groupActive ? 600 : 400,
                      color: groupActive ? theme.palette.primary.main : undefined,
                    }}
                  />
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={isActive(item.path)}
                        sx={{
                          pl: 4,
                          mx: 1,
                          my: 0.25,
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.primary.main,
                            },
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActive(item.path)
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            minWidth: 36,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: isActive(item.path) ? 600 : 400,
                            fontSize: '0.875rem',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <PageSearch />
          </Box>

          {!ENV.isProd && (
            <Chip color="warning" label={ENV.current} size="small" sx={{ mr: 1 }} variant="outlined" />
          )}

          {tenantIdData?.tenantId && (
            <TenantSwitcher currentTenantId={tenantIdData.tenantId} />
          )}

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            {user?.picture ? (
              <Avatar src={user.picture} sx={{ width: 32, height: 32 }} />
            ) : (
              <AccountIcon />
            )}
          </IconButton>
          
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'User'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleToggleTheme}>
              <ListItemIcon>
                {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: theme.palette.background.default,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};